import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import NotificationBell from './NotificationBell';
import {
  FaUser, FaWallet, FaStore, FaVideoSlash, FaSignOutAlt, FaChevronDown, FaTicketAlt,
  FaMusic, FaBook, FaQuestionCircle, FaSearch, FaVideo, FaGraduationCap, FaChalkboardTeacher,
  FaRobot, FaDumbbell, FaWhatsapp, FaComments, FaUserFriends, FaEnvelope, FaComment, FaTimes
} from 'react-icons/fa';
import ProfileImageUpload from './ProfileImageUpload';
import SearchBar from './SearchBar';

function Header() {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionDaysLeft, setSubscriptionDaysLeft] = useState<number | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const user = getCurrentUser();

  // Chat Icon Component with Unread Count
  const ChatIcon = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (!user?.id) return;
      loadUnreadCount();

      // Subscribe to new messages
      const channel = supabase
        .channel(`unread-messages-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'private_messages',
            filter: `receiver_id=eq.${user.id}`,
          },
          () => {
            loadUnreadCount();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'private_messages',
            filter: `receiver_id=eq.${user.id}`,
          },
          () => {
            loadUnreadCount();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }, [user?.id]);

    const loadUnreadCount = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        const { count, error } = await supabase
          .from('private_messages')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', user.id)
          .eq('is_read', false);

        if (error) {
          console.error('Error loading unread count:', error);
          setUnreadCount(0);
          return;
        }

        setUnreadCount(count || 0);
      } catch (error) {
        console.error('Error loading unread messages:', error);
        setUnreadCount(0);
      } finally {
        setLoading(false);
      }
    };

    const handleChatClick = () => {
      setShowProfileMenu(false);
      navigate('/messages');
    };

    return (
      <button
        onClick={handleChatClick}
        className="relative p-2 rounded-full hover:bg-white/10 text-white hover:text-yellow-400 transition-all duration-300"
        aria-label="Messages"
      >
        <FaComment className="w-5 h-5" />
        
        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold shadow-lg animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
        
        {/* Loading Indicator */}
        {loading && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
        )}
      </button>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
      fetchProfileImage();
    }
  }, [user]);

  // Fetch the current profile image from the database
  const fetchProfileImage = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('stripeusers')
        .select('profile_image')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile image:', error);
        return;
      }

      if (data?.profile_image) {
        setProfileImage(data.profile_image);
        // Also update localStorage to keep it in sync
        const updatedUser = { ...user, profile_image: data.profile_image };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('stripepracticesubscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .gte('valid_until', new Date().toISOString())
        .order('valid_until', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error checking subscription:', error);
        return;
      }

      if (data) {
        setHasSubscription(true);

        // Calculate days left
        const validUntil = new Date(data.valid_until);
        const today = new Date();
        const diffTime = validUntil.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setSubscriptionDaysLeft(diffDays);
      } else {
        setHasSubscription(false);
        setSubscriptionDaysLeft(null);
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      if (user?.id) {
        // mark offline (best effort)
        const { error } = await supabase
          .from('stripeusers')
          .update({ is_online: false })
          .eq('id', user.id);
        if (error) console.warn('Failed to update is_online:', error);
      }

      // fully end the Supabase session (all tabs)
      await supabase.auth.signOut({ scope: 'global' });

      // clear local caches
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');

      // remove any OAuth hash fragments so a refresh can't resurrect a session
      if (
        window.location.hash.includes('access_token') ||
        window.location.hash.includes('refresh_token')
      ) {
        history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }

      setShowProfileMenu(false);

      // small delay to avoid racing auth listeners
      await new Promise((r) => setTimeout(r, 50));

      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('Logout error:', error);
      toast.error(error?.message || 'Failed to sign out');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleProfileUpdate = (imageUrl: string) => {
    if (user) {
      setProfileImage(imageUrl);
      const updatedUser = { ...user, profile_image: imageUrl };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setShowProfileUpload(false);
    }
  };

  return (
    <header className="bg-black/95 backdrop-blur-lg shadow-lg border-b border-gray-800/50 fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/amalieaispanishlogo.png"
                alt="Langrigo Learning Platform"
                className="h-10 w-auto animated-logo hover:scale-105 transition-transform duration-300"
              />
            </Link>
          </div>

          {/* Center Navigation - Desktop Only */}
          {user && (
            <div className="hidden lg:flex items-center space-x-8 flex-1 justify-center">
              <Link
                to="/songs"
                className="group flex items-center space-x-2 px-4 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium"
              >
                <FaMusic className="w-4 h-4 text-yellow-400 group-hover:scale-110 transition-transform duration-300" />
                <span>Songs</span>
              </Link>
              
              <Link
                to="/stories"
                className="group flex items-center space-x-2 px-4 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium"
              >
                <FaBook className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform duration-300" />
                <span>Stories</span>
              </Link>
              
              <Link
                to="/teacher-chat"
                className="group flex items-center space-x-2 px-4 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium"
              >
                <FaRobot className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform duration-300" />
                <span>AI</span>
              </Link>
              
              <Link
                to="/learners"
                className="group flex items-center space-x-2 px-4 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium"
              >
                <FaUser className="w-4 h-4 text-green-400 group-hover:scale-110 transition-transform duration-300" />
                <span>Learners</span>
              </Link>
              
              <Link
                to="/friends"
                className="group flex items-center space-x-2 px-4 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium"
              >
                <FaUserFriends className="w-4 h-4 text-pink-400 group-hover:scale-110 transition-transform duration-300" />
                <span>Friends</span>
              </Link>
            </div>
          )}

          {/* Right Side - Actions & Profile */}
          {user ? (
            <div className="flex items-center space-x-3">
              {/* Search Bar - Desktop Only */}
              <div className="hidden lg:block">
                <div className="relative">
                  <SearchBar className="w-64" />
                </div>
              </div>
              
              {/* Action Icons */}
              <div className="flex items-center space-x-2">
                {/* Mobile Search Button */}
                <button
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  className="lg:hidden p-2 rounded-full hover:bg-white/10 text-white hover:text-yellow-400 transition-all duration-300"
                  aria-label="Search"
                >
                  <FaSearch className="w-5 h-5" />
                </button>
                
                {/* Chat Icon */}
                <ChatIcon />
                
                {/* Notification Bell */}
                <NotificationBell />
              </div>
              
              {/* Profile Menu */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 p-1 rounded-full hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center overflow-hidden border-2 border-white/20 group-hover:border-yellow-400/50 transition-all duration-300">
                    {(profileImage ?? user.profile_image) ? (
                      <img
                        src={profileImage ?? user.profile_image ?? ''}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-black font-bold text-sm">
                        {user.email.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <FaChevronDown className="w-3 h-3 text-white/60 group-hover:text-white transition-colors duration-300 hidden sm:block" />
                </button>

                {/* Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-80 md:w-64 bg-gray-900/95 backdrop-blur-lg rounded-xl shadow-2xl py-2 ring-1 ring-white/10 border border-gray-700/50">
                    {/* Mobile Search */}
                    <div className="block lg:hidden p-4 border-b border-gray-700/50">
                      <SearchBar isMobile={true} className="w-full" />
                    </div>

                    {/* Profile Section */}
                    <div className="px-4 py-3 border-b border-gray-700/50">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center overflow-hidden">
                          {(profileImage ?? user.profile_image) ? (
                            <img src={profileImage ?? user.profile_image ?? ''} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-black font-bold">{user.email.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {user.full_name || user.email}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{user.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Navigation Links */}
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          navigate('/profile');
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                      >
                        <FaUser className="w-4 h-4 mr-3 text-gray-400" />
                        Profile Settings
                      </button>

                      {/* Mobile Navigation Links */}
                      <div className="lg:hidden border-t border-gray-700/50 mt-2 pt-2">
                        <Link
                          to="/songs"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaMusic className="w-4 h-4 mr-3 text-yellow-400" />
                          Songs
                        </Link>

                        <Link
                          to="/stories"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaBook className="w-4 h-4 mr-3 text-blue-400" />
                          Stories
                        </Link>

                        <Link
                          to="/teacher-chat"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaRobot className="w-4 h-4 mr-3 text-purple-400" />
                          AI
                        </Link>

                        <Link
                          to="/learners"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaUser className="w-4 h-4 mr-3 text-green-400" />
                          Learners
                        </Link>

                        <Link
                          to="/friends"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaUserFriends className="w-4 h-4 mr-3 text-pink-400" />
                          Friends
                        </Link>

                        <Link
                          to="/messages"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaEnvelope className="w-4 h-4 mr-3 text-blue-400" />
                          Messages
                        </Link>
                      </div>

                      {/* Utility Links */}
                      <div className="border-t border-gray-700/50 mt-2 pt-2">
                        <Link
                          to="/wallet"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaWallet className="w-4 h-4 mr-3 text-green-400" />
                          Wallet
                        </Link>

                        <Link
                          to="/coupons"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaTicketAlt className="w-4 h-4 mr-3 text-yellow-400" />
                          Coupons
                        </Link>

                        <Link
                          to="/whatsapp"
                          className="flex items-center px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors duration-200"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FaWhatsapp className="w-4 h-4 mr-3 text-green-500" />
                          WhatsApp
                        </Link>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-gray-700/50 mt-2 pt-2">
                        <button
                          onClick={handleLogout}
                          disabled={loggingOut}
                          className="flex w-full items-center px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50 transition-all duration-200"
                        >
                          <FaSignOutAlt className="w-4 h-4 mr-3" />
                          {loggingOut ? 'Signing out…' : 'Sign out'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <nav className="hidden md:flex items-center space-x-6">
              <Link
                to="/login"
                className="text-white/80 hover:text-white px-4 py-2 rounded-xl hover:bg-white/10 transition-all duration-300 font-medium"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-2 rounded-xl font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Sign up
              </Link>
            </nav>
          )}
        </div>
      </div>

      {/* Mobile Search Overlay */}
      {showMobileSearch && user && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-lg border-b border-gray-800/50 p-4 z-40">
          <div className="flex items-center space-x-3">
            <div className="flex-1">
              <SearchBar isMobile={true} className="w-full" />
            </div>
            <button
              onClick={() => setShowMobileSearch(false)}
              className="p-2 rounded-full hover:bg-white/10 text-white"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Profile Image Upload Modal */}
      {showProfileUpload && user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-black rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Update Profile Picture</h3>
              <button
                onClick={() => setShowProfileUpload(false)}
                className="text-white hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <ProfileImageUpload
              userId={user.id}
              currentImage={user.profile_image}
              onImageUpdate={handleProfileUpdate}
            />
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;